import React from 'react';

import { RaRecord, required } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { TextField } from 'shared/mui/TextField/TextField';
import { Typography } from 'shared/mui/Typography';
import {
  AutocompleteInput,
  BooleanInput,
  WatchedTextInput,
  AutocompleteMultiselectInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { CommissionsCalculationMethodInput } from 'shared/widgets/CommissionsCalculationMethodInput/CommissionsCalculationMethodInput';
import { useCalculationMethodVisibility } from 'shared/widgets/CommissionsCalculationMethodInput/hooks/useCalculationMethodVisibility';
import { useResetCalculationMethodOnHide } from 'shared/widgets/CommissionsCalculationMethodInput/hooks/useResetCalculationMethodOnHide';

import { BoxStyled } from './CommissionsCreateNewVersionForm.styled';
import { CommissionsNameInput } from '../../components/CommissionsNameInput';

export type CommissionsCreateNewVersionFormValues = {
  name: string;
  merchant: string;
  provider: string | null;
  currency: string;
  transactionTypes: string[];
  rate: {
    zeroed: boolean;
    percent: string | null;
    fix: string | null;
    min: string | null;
    max: string | null;
  };
};

export const CommissionsCreateNewVersionForm = () => {
  const { formState, watch, trigger } = useFormContext<CommissionsCreateNewVersionFormValues>();

  const zeroedWatched = watch('rate.zeroed');
  const isCalculationMethodVisible = useCalculationMethodVisibility();
  useResetCalculationMethodOnHide(isCalculationMethodVisible);

  const triggerPercentValidation = () =>
    trigger(['rate.percent', 'rate.fix', 'rate.max', 'rate.min']);

  return (
    <Box sx={{ paddingY: '24px', width: '100%' }}>
      <BoxStyled>
        <Typography fontWeight={500} sx={{ gridColumn: ' 1 / span 4' }} variant="body1">
          Основные настройки
        </Typography>
        <CommissionsNameInput
          fullWidth
          label="Название"
          size="medium"
          source="name"
          sx={{ gridColumn: ' 1 / span 4' }}
          validate={required()}
          variant="outlined"
        />
        <ReferenceInput
          filter={{ archive: false }}
          perPage={100}
          reference="merchants"
          source="merchant"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            label="Мерчант"
            optionText="name"
            readOnly={true}
            size="small"
            variant="outlined"
          />
        </ReferenceInput>
        <ReferenceInput perPage={100} reference="providers" source="provider">
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            isRequired
            label="Провайдер"
            optionText="name"
            readOnly={true}
            size="small"
            source="provider"
            variant="outlined"
          />
        </ReferenceInput>
        <ReferenceArrayInput
          perPage={100}
          reference="transaction_types/commission/list"
          source="transactionTypes"
        >
          <AutocompleteMultiselectInput
            fullWidth
            isRequired
            label="Типы транзакций"
            name="transactionTypes"
            optionText="name"
            readOnly={true}
            size="small"
            sx={{
              '.MuiTextField-root': {
                marginTop: 0,
              },
            }}
          />
        </ReferenceArrayInput>
        <ReferenceInput
          filter={{ active: true }}
          perPage={100}
          reference="currencies"
          sort={{ field: 'id', order: 'ASC' }}
          source="currency"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            fullWidth
            isRequired
            name="currency"
            optionText={(record: RaRecord) => `${record?.alphaCode}, ${record?.name}`}
            readOnly={true}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!!formState.errors?.currency}
                helperText={(formState.errors?.currency?.message as string) || ' '}
                label="Валюта"
                size="small"
                variant="outlined"
              />
            )}
            variant="outlined"
          />
        </ReferenceInput>
      </BoxStyled>
      <BoxStyled>
        <Typography fontWeight={500} sx={{ gridColumn: ' 1 / span 2' }} variant="body1">
          Условия комиссии
        </Typography>
        <BooleanInput
          defaultValue={false}
          label="Нулевая комиссия"
          size="small"
          source="rate.zeroed"
          sx={{ gridColumn: ' 4 / span 1', alignContent: 'end' }}
          variant="outlined"
        />
        <WatchedTextInput
          disabled={zeroedWatched}
          fullWidth
          label="Процент"
          onChange={triggerPercentValidation}
          size="small"
          source="rate.percent"
          sx={{ maxWidth: 'initial' }}
          variant="outlined"
          watchField="rate.zeroed"
        />
        <WatchedTextInput
          disabled={zeroedWatched}
          fullWidth
          label="Фиксированная"
          onChange={triggerPercentValidation}
          size="small"
          source="rate.fix"
          sx={{ maxWidth: 'initial' }}
          variant="outlined"
          watchField="rate.zeroed"
        />
        <WatchedTextInput
          disabled={zeroedWatched}
          fullWidth
          label="Максимальная"
          onChange={triggerPercentValidation}
          size="small"
          source="rate.max"
          sx={{ maxWidth: 'initial' }}
          variant="outlined"
          watchField="rate.zeroed"
        />
        <WatchedTextInput
          disabled={zeroedWatched}
          fullWidth
          label="Минимальная"
          onChange={triggerPercentValidation}
          size="small"
          source="rate.min"
          sx={{ maxWidth: 'initial' }}
          variant="outlined"
          watchField="rate.zeroed"
        />
      </BoxStyled>
      {isCalculationMethodVisible && (
        <Box sx={{ mt: '10px' }}>
          <CommissionsCalculationMethodInput />
        </Box>
      )}
    </Box>
  );
};
